import type { TimerState } from '../../../hooks/useTransactionTimer'

import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'

import { setTransactionResolution } from '@/components/Footer/hooks/useTransactionResolution'
import {
  commitTransaction,
  goToNextRevision,
  goToPreviousRevision,
  rollbackTransaction,
} from '@/features/domain/transaction'
import { useAppDispatch } from '@/store'

import { useIsLoading } from '@/atoms'
import { selectData } from '../cursors/selectData'

export const useTransactionControls = (transactionState: TimerState, onResetTimer: () => void) => {
  const [actionInProgress, setActionInProgress] = useState(false)
  const dispatch = useAppDispatch()

  const { prevRevision, nextRevision } = useSelector(selectData)
  const serverActionPrevented = useIsLoading()

  const ready = transactionState === 'ready'
  const paused = transactionState === 'paused'

  const handleOnUndo = useCallback(async () => {
    setActionInProgress(true)
    await dispatch(goToPreviousRevision())
    onResetTimer()
    setActionInProgress(false)
  }, [dispatch, onResetTimer])

  const handleOnRedo = useCallback(async () => {
    setActionInProgress(true)
    await dispatch(goToNextRevision())
    onResetTimer()
    setActionInProgress(false)
  }, [dispatch, onResetTimer])

  const handleOnRollback = useCallback(async () => {
    setActionInProgress(true)
    setTransactionResolution('rollback')
    await dispatch(rollbackTransaction())
    setActionInProgress(false)
  }, [dispatch])

  const handleOnResetTimer = useCallback(async () => {
    onResetTimer()
  }, [onResetTimer])

  const handleOnApply = useCallback(async () => {
    setActionInProgress(true)
    setTransactionResolution('commit')
    await dispatch(commitTransaction())
    setActionInProgress(false)
  }, [dispatch])

  return {
    handleOnApply,
    handleOnRedo,
    handleOnResetTimer,
    handleOnRollback,
    handleOnUndo,

    disableSave: actionInProgress || paused || ready || serverActionPrevented,
    disableRedo: actionInProgress || nextRevision < 0 || serverActionPrevented,
    disableUndo: actionInProgress || prevRevision < 0 || serverActionPrevented,
    disableRollback: actionInProgress || paused || ready || serverActionPrevented,
  }
}
